import { PictureOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  Avatar,
  Col,
  Image,
  Row,
  Space,
  Card,
  Button,
  Tabs,
  Dropdown,
  Tag,
} from 'antd';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import productApi from '../../api/product';
import openNotification from '../../components/Toastr';
import { getErrorMessage } from '../../api/api';
import './catalogue.css';
import getLinkToStore from '../home/components/linkToStore';
import CheckboxDropdown from './components/CheckboxDropdown';
import ReviewsTable from './components/ReviewsTable';
import ReviewsFilter from './components/ReviewsFilter';
import reviewsAPI from '../../api/reviews';
import StarRating from './components/StarRating';

function Reviews() {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredProductsLoading, setFilteredProductsLoading] = useState(false);
  const [rowKeys, setRowKeys] = useState([]);
  const [skus, setSkus] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [filter, setFilter] = useState({
    marketplace: null,
    statuses: null,
    fulfillmentType: null,
    sku: null,
    title: null,
    limit: 50,
    page: 1,
  });
  const [totalResults, setTotalResults] = useState(0);
  const [totalParents, setTotalParents] = useState(null);
  const [initialTotal, setInitialTotal] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [reviewsSetting, setReviewsSetting] = useState({});

  const [globalRules, setGlobalRules] = useState(false);

  const { t } = useTranslation();
  const language = useSelector((store) => store.Session.language);
  const history = useHistory();

  const selectedTotal = selectAll
    ? initialTotal - Number(productIds?.length)
    : Number(productIds?.length);

  const expandIcon = ({ expanded, onExpand, record }) =>
    record?.children?.length > 0 ? (
      <Button type="link" onClick={(e) => onExpand(record, e)}>
        {expanded ? <UpOutlined /> : <DownOutlined />}
      </Button>
    ) : null;

  const setDefaultFilterValues = () => {
    setRowKeys([]);
    setSkus([]);
    setProductIds([]);
    setSelectAll(false);
  };

  const getProductReviews = async () => {
    await reviewsAPI.getReviewConfig(filter).then((result) => {
      if (!result?.data || result?.data?.length === 0) {
        setReviewsSetting({});
      } else {
        result?.data.sort((a, b) => {
          if (a.global === b.global) return 0;
          return a.global ? -1 : 1;
        });

        const settings = {};

        result?.data.forEach((item) => {
          if (item.global === true && item.skuExceptions?.length === 0) {
            settings.default = item.sendAfterDays;
            setGlobalRules(true);
          } else if (item.global === true && item.skuExceptions?.length > 0) {
            settings.default = item.sendAfterDays;
            item.skuExceptions.forEach((sku) => {
              settings[sku] = 0;
            });
            setGlobalRules(true);
          } else {
            settings[item.sku] = item.sendAfterDays;
          }
        });

        setReviewsSetting(settings);
      }
    });
  };

  useEffect(async () => {
    if (!filter.marketplace) return;
    setFilteredProductsLoading(true);
    await productApi
      .buildProductsTree(filter)
      .then((result) => {
        const { data } = result;
        setFilteredProducts(data.rows);
        setTotalResults(data.totalParents + data.totalChildren);
        if (!filter.sku && !filter.statuses && !filter.title) {
          setInitialTotal(data.totalParents + data.totalChildren);
        }
        setTotalParents(data.totalParents);
        setFilteredProductsLoading(false);
        const currentPageRows = [
          ...data.rows.map((obj) => obj?.id),
          ...data.rows.flatMap((obj) =>
            obj.children?.map((child) => child?.id)
          ),
        ].filter((id) =>
          selectAll ? !productIds.includes(id) : productIds.includes(id)
        );
        setRowKeys(currentPageRows);
      })
      .catch((error) => {
        openNotification({ status: false, content: getErrorMessage(error) });
        setFilteredProductsLoading(false);
      });
    await getProductReviews();
  }, [filter]);

  useEffect(() => {
    if (productIds.length === initialTotal && initialTotal > 0) {
      setSelectAll(!selectAll);
      setSkus([]);
      setProductIds([]);
    }
  }, [productIds]);

  const getRecordIdentifier = (record) => {
    if (record.asin) {
      return `ASIN: ${record?.asin}`;
    }
    return `Identificador: ${record?.externalId}`;
  };

  const renderProductColumn = (value, record) => {
    const imgUrlGroupedMkts =
      record?.mainImages?.length > 0 ? record?.mainImages[0]?.url : null;

    return (
      <Row align="middle" justify="start">
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Space size="middle">
              {imgUrlGroupedMkts ? (
                <Avatar
                  shape="square"
                  size={55}
                  style={{ backgroundColor: '#FFFFFF' }}
                  src={
                    (record?.children?.length === 0 || record.isVariant) &&
                    imgUrlGroupedMkts ? (
                      <Image src={`${imgUrlGroupedMkts}`} />
                    ) : null
                  }
                />
              ) : (
                <Avatar
                  shape="square"
                  size={55}
                  style={{ backgroundColor: '#FFFFFF' }}
                  src={
                    record?.children?.length === 0 || record?.isVariant ? (
                      <PictureOutlined />
                    ) : null
                  }
                />
              )}
              <Space direction="vertical" style={{ textAlign: 'left' }}>
                <p
                  style={{
                    marginBottom: -10,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getLinkToStore[record?.marketplace](record)}
                </p>
                <p
                  style={{
                    marginBottom: -4,
                    fontSize: '12px',
                  }}
                >
                  {getRecordIdentifier(record)}
                </p>
              </Space>
            </Space>
          </div>
        </Col>
      </Row>
    );
  };

  const items = [
    {
      key: '5',
      label: t('catalogue.table.daysAfter', { days: 5 }),
    },
    {
      key: '15',
      label: t('catalogue.table.daysAfter', { days: 15 }),
    },
    {
      key: '30',
      label: t('catalogue.table.daysAfter', { days: 30 }),
    },
    {
      key: '0',
      label: t('catalogue.table.noReview'),
    },
  ];

  const handleSingleSetting = async (e, record) => {
    setFilteredProductsLoading(true);
    if (e.key === '0') {
      await reviewsAPI
        .deleteReviewConfig({
          all: false,
          skus: [record.defaultCode],
          marketplace: filter.marketplace,
        })
        .then(() => {
          openNotification({
            status: true,
            content: t('catalogue.table.reviewConfigSuccess'),
          });
        })
        .catch((error) => {
          openNotification({ status: false, content: getErrorMessage(error) });
          setFilteredProductsLoading(false);
        });
    } else {
      await reviewsAPI
        .upsertReviewConfig({
          all: false,
          skus: [record.defaultCode],
          marketplace: filter.marketplace,
          sendAfterDays: Number(e.key),
        })
        .then(() => {
          openNotification({
            status: true,
            content: t('catalogue.table.reviewConfigSuccess'),
          });
        })
        .catch((error) => {
          openNotification({ status: false, content: getErrorMessage(error) });
          setFilteredProductsLoading(false);
        });
    }
    setRowKeys([]);
    setSkus([]);
    setProductIds([]);
    setSelectAll(false);
    await getProductReviews();
    setFilteredProductsLoading(false);
  };

  const renderActionsColumn = (value, record) => {
    return (
      <Row style={{ justifySelf: 'center' }}>
        <Col span={24}>
          <Dropdown.Button
            menu={{
              items,
              onClick: (e) => handleSingleSetting(e, record),
            }}
            icon={<DownOutlined style={{ fontSize: 14 }} />}
          >
            {(() => {
              if (Object.keys(reviewsSetting).length === 0) {
                return t('catalogue.table.noReview');
              }
              if (
                reviewsSetting[record.defaultCode] &&
                reviewsSetting[record.defaultCode] > 0
              ) {
                return t('catalogue.table.daysAfter', {
                  days: reviewsSetting[record.defaultCode],
                });
              }
              if (
                reviewsSetting[record.defaultCode] !== undefined &&
                reviewsSetting[record.defaultCode] === 0
              ) {
                return t('catalogue.table.noReview');
              }
              return t('catalogue.table.daysAfter', {
                days: reviewsSetting.default,
              });
            })()}
          </Dropdown.Button>
        </Col>
      </Row>
    );
  };

  const mergeArraysWithoutDuplicates = (array1, array2) => [
    ...new Set([...array1, ...array2]),
  ];

  const mergeParentChildKeys = (array, key) => {
    return new Set([
      ...array.map((obj) => obj?.[key]),
      ...array.flatMap((obj) => obj.children?.map((child) => child?.[key])),
    ]);
  };

  const handleSingleSelection = (selectedRows) => {
    const selectedRowsOnlyParents =
      selectedRows?.filter((obj) => obj && !obj?.isVariant) || [];
    const selectedSkus = mergeParentChildKeys(
      selectedRowsOnlyParents,
      'defaultCode'
    );
    const pageSkus = mergeParentChildKeys(filteredProducts, 'defaultCode');
    const selectedIds = mergeParentChildKeys(selectedRowsOnlyParents, 'id');
    const pageIds = mergeParentChildKeys(filteredProducts, 'id');
    setRowKeys([...selectedIds]);
    if (selectAll) {
      setSkus(
        mergeArraysWithoutDuplicates(
          skus.filter((s) => !pageSkus.has(s)),
          [...pageSkus].filter((s) => !selectedSkus.has(s))
        )
      );
      setProductIds(
        mergeArraysWithoutDuplicates(
          productIds.filter((i) => !pageIds.has(i)),
          [...pageIds].filter((i) => !selectedIds.has(i))
        )
      );
    } else {
      setSkus(
        mergeArraysWithoutDuplicates(
          skus.filter((s) => !pageSkus.has(s)),
          selectedSkus
        )
      );
      setProductIds(
        mergeArraysWithoutDuplicates(
          productIds.filter((i) => !pageIds.has(i)),
          selectedIds
        )
      );
    }
  };

  const pagination = {
    defaultPageSize: 50,
    pageSize: filter.limit,
    pageSizeOptions: [10, 20, 50],
    current: filter.page,
    onChange: (page) => {
      setFilter({ ...filter, page });
    },
    onShowSizeChange: (limit) => setFilter({ ...filter, limit, page: 1 }),
    total: totalResults,
  };

  const expandable = {
    expandedRowRender: (record) => record,
    rowExpandable: (record) => record?.children?.length > 0,
    expandIconColumnIndex: 1,
    expandIcon,
  };

  const rowSelectionByMkt = {
    type: 'checkbox',
    getCheckboxProps: (record) => {
      const checkboxProps = {};
      if (record.isVariant) {
        checkboxProps.disabled = true;
      }
      return checkboxProps;
    },
    selectedRowKeys: rowKeys,
    preserveSelectedRowKeys: true,
    columnTitle: (
      <CheckboxDropdown
        initialTotal={initialTotal}
        totalResults={totalResults}
        selectAll={selectAll}
        setSelectAll={setSelectAll}
        rowKeys={rowKeys}
        setRowKeys={setRowKeys}
        productIds={productIds}
        setProductIds={setProductIds}
        skus={skus}
        setSkus={setSkus}
        filteredProducts={filteredProducts}
      />
    ),
    onChange: (selectedRowsKeys, selectedRows, info) => {
      if (info.type === 'single') {
        handleSingleSelection(selectedRows);
      }
    },
  };

  const handleMenuClick = async (e) => {
    setFilteredProductsLoading(true);
    if (e.key === '0') {
      await reviewsAPI
        .deleteReviewConfig({
          all: selectAll,
          skus,
          marketplace: filter.marketplace,
        })
        .then(() => {
          openNotification({
            status: true,
            content: t('catalogue.table.reviewConfigSuccess'),
          });
        })
        .catch((error) => {
          openNotification({ status: false, content: getErrorMessage(error) });
          setFilteredProductsLoading(false);
        });
    } else {
      await reviewsAPI
        .upsertReviewConfig({
          all: selectAll,
          skus,
          marketplace: filter.marketplace,
          sendAfterDays: Number(e.key),
        })
        .then(() => {
          openNotification({
            status: true,
            content: t('catalogue.table.reviewConfigSuccess'),
          });
        })
        .catch((error) => {
          openNotification({ status: false, content: getErrorMessage(error) });
          setFilteredProductsLoading(false);
        });
    }
    setRowKeys([]);
    setSkus([]);
    setProductIds([]);
    setSelectAll(false);
    await getProductReviews();
    setFilteredProductsLoading(false);
  };

  const renderReviewScoreColumn = (value, record) => {
    return (
      <Row align="middle" justify="start">
        <Col span={24}>
          <Space direction="vertical" style={{ textAlign: 'center' }}>
            <p
              style={{
                marginBottom: -10,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {value && <StarRating value={value ?? 0} />}
            </p>
            <p
              style={{
                marginBottom: -10,
                fontSize: '12px',
              }}
            >
              {value && record.reviewCount
                ? `${record.reviewCount} ${t(
                    'menu.catalogue.reviews'
                  ).toLowerCase()}`
                : t('catalogue.table.withoutReviews')}
            </p>
            <p
              style={{
                marginBottom: -10,
                fontSize: '10px',
                color: '#646464',
              }}
            >
              {record.lastRatingSyncDate
                ? `${t('catalogue.table.lastUpdateRating')}: ${moment(
                    record.lastRatingSyncDate
                  )
                    .locale(language)
                    .format('MMM DD, YY')}`
                : `-`}
            </p>
          </Space>
        </Col>
      </Row>
    );
  };

  return (
    <div className="content-div container-products">
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        <Card>
          <ReviewsFilter
            filter={filter}
            setFilter={setFilter}
            setDefaultFilterValues={setDefaultFilterValues}
          />
        </Card>
        <Card>
          {globalRules && (
            <Row style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
              <Col>
                <Space>
                  <Tag
                    color="#04dfa152"
                    style={{
                      marginRight: 0,
                      fontWeight: 400,
                      color: '#010c33',
                      borderColor: '#04dfa0',
                    }}
                    bordered
                  >
                    {t('catalogue.baseConfiguration')}:{' '}
                    {(() => {
                      if (reviewsSetting?.default) {
                        return t('catalogue.table.daysAfter', {
                          days: reviewsSetting?.default,
                        });
                      }
                      return t('catalogue.table.noReview');
                    })()}
                  </Tag>
                </Space>
              </Col>
            </Row>
          )}

          <Row style={{ justifyContent: 'space-between' }}>
            <Col>
              <div style={{ color: '#00E5A6' }}>
                {selectAll || productIds?.length > 0
                  ? `${t(
                      'catalogue.select.selectedListings'
                    )}: ${selectedTotal}`
                  : ''}
              </div>
            </Col>
            <Col>
              <Space>
                <Button
                  className="btn-basic"
                  onClick={() => history.push('/catalogo/review-requests')}
                >
                  Ver estadística
                </Button>
                <Dropdown.Button
                  menu={{
                    items,
                    onClick: handleMenuClick,
                  }}
                  icon={<DownOutlined />}
                  disabled={rowKeys.length === 0}
                >
                  {t('catalogue.setMultipleReviews')}
                </Dropdown.Button>
              </Space>
            </Col>
          </Row>
          <Tabs>
            <Tabs.TabPane
              tab={`${totalResults || '-'} ${t('common.listing', {
                count: totalResults,
              }).toLowerCase()} (${totalParents || '-'} ${t('common.parent', {
                count: totalParents,
              }).toLowerCase()})`}
              key="item-1"
            >
              <Row className="products-table">
                <Col span={24}>
                  <ReviewsTable
                    filteredProducts={filteredProducts}
                    loading={filteredProductsLoading}
                    pagination={pagination}
                    expandable={expandable}
                    rowSelection={rowSelectionByMkt}
                    renderProductColumn={renderProductColumn}
                    renderActionsColumn={renderActionsColumn}
                    renderReviewScoreColumn={renderReviewScoreColumn}
                  />
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Space>
    </div>
  );
}

export default Reviews;
